import { PencilIcon } from "@heroicons/react/24/outline";
import { Formik, FormikProps } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import * as Yup from "yup";

import { ComparisonSegment, PopulationSegment, PrimarySegment } from "..";
import {
  BlankButton,
  CustomDropdownWithChevron,
  ErrorToast,
  PrimaryButton,
  SuccessToast,
} from "../../components";
import { useDialogContext } from "../../contexts/DialogContext";
import { useUIContext } from "../../contexts/UIContext";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { customAnalysisFormSlice } from "../../redux/slices/customAnalysisFormSlice";
import { customAnalysisSlice } from "../../redux/slices/customAnalysisSlice";

import { useCreateRequestedAnalysisMutation } from "../../services";
import { customSelectStyles } from "../../shared/Constants";
import { joinClassNames } from "../../shared/Functions";
import { CustomAnalysisFormValues } from "../../types";

export const movementTypeOptions = [
  { value: "hitting", label: "Swing", id: 1 },
  { value: "pitching", label: "Throw", id: 2 },
];

export const handOptions = [
  { value: "righty", label: "Right Hand", id: "RHA" },
  { value: "lefty", label: "Left Hand", id: "LHA" },
];

export const CustomAnalysisForm: React.FC = (): JSX.Element => {
  const navigate = useNavigate();

  // State and AppContext
  const [shouldShowSegments, setShouldShowSegments] = useState(false);
  const { setDisplayBack } = useUIContext();
  const { setDialogState } = useDialogContext();
  const [primarySegmentDefined, setPrimarySegmentDefined] = useState(false);
  const [populationSegmentDefined, setPopulationSegmentDefined] =
    useState(false);
  const [comparisonSegmentDefined, setComparisonSegmentDefined] =
    useState(false);
  const [editPrimarySegment, setEditPrimarySegment] = useState(false);
  const [editPopulationSegment, setEditPopulationSegment] = useState(false);
  const [editComparisonSegment, setEditComparisonSegment] = useState(false);
  const [disableDropdowns, setDisableDropdowns] = useState(false);

  // RTK slice actions
  const dispatch = useAppDispatch();
  const { storeCustomAnalysisFormState, clearCustomAnalysisFormState } =
    customAnalysisFormSlice.actions;
  const { clearCustomAnalysisState } = customAnalysisSlice.actions;

  // Redux calls
  const customAnalysisFormState = useAppSelector(
    (state) => state.customAnalysisForm,
  );
  const customAnalysisState = useAppSelector((state) => state.customAnalysis);

  // RTK Mutation
  const [createCustomAnalysis] = useCreateRequestedAnalysisMutation();

  const { primarySegmentData, populationSegmentData, comparisonSegmentData } =
    customAnalysisState;

  const errorToast = (message: string) =>
    toast.custom(<ErrorToast message={message} classNames="mt-modal" />, {
      id: "createAnalysisError",
    });

  const successToast = (message: string) =>
    toast.custom(<SuccessToast message={message} classNames="mt-modal" />);

  const formRef = useRef<FormikProps<CustomAnalysisFormValues>>(null);

  const formValues = {
    name: "",
    mocapType: { value: "", label: "", id: 0 },
    hand: { value: "", label: "", id: "" },
    movementType: { value: "", label: "", id: 0 },
    primarySegment: {
      dateRange: [],
      individualDates: [],
      dateCriteria: { value: "", label: "" },
      criteria: [],
      id: 0,
    },
    populationSegment: {
      dateRange: [],
      individualDates: [],
      dateCriteria: { value: "", label: "" },
      criteria: [],
      id: 0,
    },
    comparisonSegment: {
      dateRange: [],
      individualDates: [],
      dateCriteria: { value: "", label: "" },
      criteria: [],
      id: 0,
    },
  };

  const handleRequestAnalysis = () => {
    const requestBody: {
      name: string;
      primary_analysis_segment_id: number | null;
      status: string;
      population_analysis_segment_id?: number | null;
      comparison_analysis_segment_id?: number | null;
    } = {
      name: customAnalysisFormState.name,
      primary_analysis_segment_id: primarySegmentData.analysisSegmentId,
      status: "requested",
    };

    if (populationSegmentData.analysisSegmentId !== null) {
      requestBody.population_analysis_segment_id =
        populationSegmentData.analysisSegmentId;
    }

    if (comparisonSegmentData.analysisSegmentId !== null) {
      requestBody.comparison_analysis_segment_id =
        comparisonSegmentData.analysisSegmentId;
    }

    createCustomAnalysis(requestBody)
      .unwrap()
      .then(() => {
        dispatch(clearCustomAnalysisState());
        dispatch(clearCustomAnalysisFormState());
        navigate("/reports/custom");
        successToast("Your analysis has been requested.");
      })
      .catch(() => {
        errorToast(
          "There was an error requesting your analysis. Please try again.",
        );
      });
  };

  const cancelAnalysisRequest = () => {
    navigate(-1);
    dispatch(clearCustomAnalysisFormState());
    dispatch(clearCustomAnalysisState());
  };

  useEffect(() => {
    setDisplayBack && setDisplayBack(true);
  }, [setDisplayBack]);

  useEffect(() => {
    if (
      customAnalysisFormState.hand &&
      customAnalysisFormState.hand.value !== "" &&
      customAnalysisFormState.movementType &&
      customAnalysisFormState.movementType.value !== ""
    ) {
      setShouldShowSegments(true);
    } else {
      setShouldShowSegments(false);
    }
  }, [customAnalysisFormState]);

  useEffect(() => {
    if (
      primarySegmentDefined ||
      populationSegmentDefined ||
      comparisonSegmentDefined
    ) {
      setDisableDropdowns(true);
    } else {
      setDisableDropdowns(false);
    }
  }, [
    primarySegmentDefined,
    populationSegmentDefined,
    comparisonSegmentDefined,
  ]);

  // Clear Redux state on page refresh (not normal navigation) and unmount
  // We use sessionStorage to detect refresh: if the marker exists on mount,
  // it means we refreshed while on this page (marker persists through refresh
  // but is removed on normal navigation away)
  useEffect(() => {
    const wasOnFormBeforeRefresh = sessionStorage.getItem(
      "customAnalysisFormMounted",
    );

    if (wasOnFormBeforeRefresh) {
      // Page was refreshed while on this form, clear state
      dispatch(clearCustomAnalysisFormState());
      dispatch(clearCustomAnalysisState());
    }

    // Mark that we're on this form
    sessionStorage.setItem("customAnalysisFormMounted", "true");

    return () => {
      // On unmount (navigation away), remove the marker and clear state
      sessionStorage.removeItem("customAnalysisFormMounted");
      dispatch(clearCustomAnalysisFormState());
      dispatch(clearCustomAnalysisState());
    };
  }, [dispatch, clearCustomAnalysisFormState, clearCustomAnalysisState]);

  return (
    <main className="relative overflow-y-auto w-full z-0">
      <div className="pb-28 w-full">
        <div className="mt-8">
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={formValues}
            validationSchema={Yup.object({
              hand: Yup.object().shape({
                value: Yup.string(),
                label: Yup.string(),
                id: Yup.string(),
              }),
              movementType: Yup.object().shape({
                value: Yup.string(),
                label: Yup.string(),
                id: Yup.number(),
              }),
              primarySegment: Yup.object().shape({
                dateRange: Yup.array().of(Yup.string()),
                dateCriteria: Yup.object().shape({
                  value: Yup.string(),
                  label: Yup.string(),
                }),
                criteria: Yup.array()
                  .min(1)
                  .of(
                    Yup.object().shape({
                      field: Yup.object().shape({
                        value: Yup.string() && Yup.string().required(),
                        label: Yup.string() && Yup.string().required(),
                      }),
                      comparison: Yup.object().shape({
                        value: Yup.string() && Yup.string().required(),
                        label: Yup.string() && Yup.string().required(),
                      }),
                      option: Yup.lazy((value) =>
                        Array.isArray(value)
                          ? Yup.array()
                              .min(1)
                              .of(
                                Yup.object().shape({
                                  value:
                                    Yup.string() && Yup.string().required(),
                                  label:
                                    Yup.string() && Yup.string().required(),
                                }),
                              )
                          : Yup.object().shape({
                              value: Yup.string() && Yup.string().required(),
                              label: Yup.string() && Yup.string().required(),
                            }),
                      ),
                    }),
                  ),
              }),
            })}
            onSubmit={() => {
              false;
            }}
          >
            {({
              values,
              setFieldValue,
              setFieldTouched,
              handleSubmit,
              isValid,
              dirty,
            }) => (
              <div>
                <form
                  className="px-4"
                  data-testid="custom-analysis-form"
                  onSubmit={handleSubmit}
                  autoComplete="off"
                  data-lpignore="true"
                >
                  <div
                    className="max-w-8xl mx-auto flex justify-between items-center pb-2"
                    data-testid="data-source-header"
                  >
                    <div className="flex justify-start items-center text-gray-900">
                      <h2
                        className={joinClassNames(
                          !customAnalysisFormState.name ? "opacity-20" : "",
                          "mt-4 px-4 text-3xl leading-6 font-bold sm:px-6 lg:px-8 lg:pb-6",
                        )}
                        data-testid="custom-analysis-name"
                      >
                        {customAnalysisFormState.name
                          ? customAnalysisFormState.name
                          : "Name Analysis..."}
                      </h2>
                      <button
                        data-testid="edit-analysis-name-button"
                        onClick={(event) => {
                          event.preventDefault();
                          setDialogState({
                            shouldOpen: true,
                            type: "analysis",
                          });
                        }}
                      >
                        <PencilIcon className="w-5 ml-4 mb-2" />
                      </button>
                    </div>
                  </div>

                  <div
                    className="max-w-3xl px-4 ml-4 flex justify-start pb-2"
                    data-testid="required-custom-analysis-params"
                  >
                    <div
                      className="w-60 mr-4"
                      data-testid="hand-dropdown-container"
                    >
                      <Select
                        name="hand-dropdown"
                        aria-labelledby="hand-aria-label"
                        inputId="hand-dropdown"
                        options={handOptions}
                        value={
                          customAnalysisFormState.hand &&
                          customAnalysisFormState.hand.value !== ""
                            ? customAnalysisFormState.hand
                            : null
                        }
                        placeholder="Select Hand"
                        isSearchable={false}
                        onChange={(hand) => {
                          if (hand) {
                            setFieldValue("hand", hand);
                            dispatch(
                              storeCustomAnalysisFormState({
                                ...customAnalysisFormState,
                                hand: hand,
                              }),
                            );
                          }
                        }}
                        styles={customSelectStyles}
                        classNamePrefix={"hand-dropdown"}
                        components={{
                          DropdownIndicator: CustomDropdownWithChevron,
                          IndicatorSeparator: () => null,
                        }}
                        onBlur={() => {
                          setFieldTouched("hand", true);
                        }}
                        isDisabled={
                          disableDropdowns || !customAnalysisFormState.name
                        }
                      />
                    </div>
                    <div
                      className="w-60"
                      data-testid="movement-type-dropdown-container"
                    >
                      <Select
                        name="movement-type-dropdown"
                        aria-labelledby="movement-type-aria-label"
                        inputId="movement-type-dropdown"
                        options={movementTypeOptions}
                        value={
                          customAnalysisFormState.movementType &&
                          customAnalysisFormState.movementType.value !== ""
                            ? customAnalysisFormState.movementType
                            : null
                        }
                        placeholder="Select Movement Type"
                        isSearchable={false}
                        onChange={(movementType) => {
                          if (movementType) {
                            setFieldValue("movementType", movementType);
                            dispatch(
                              storeCustomAnalysisFormState({
                                ...customAnalysisFormState,
                                movementType: movementType,
                              }),
                            );
                          }
                        }}
                        styles={customSelectStyles}
                        classNamePrefix={"movement-type-dropdown"}
                        components={{
                          DropdownIndicator: CustomDropdownWithChevron,
                          IndicatorSeparator: () => null,
                        }}
                        onBlur={() => {
                          setFieldTouched("movementType", true);
                        }}
                        isDisabled={
                          disableDropdowns || !customAnalysisFormState.name
                        }
                      />
                    </div>
                  </div>

                  <div
                    className="max-w-8xl pt-6 mx-8"
                    data-testid="player-group-segments-container"
                  >
                    <PrimarySegment
                      shouldShowSegments={shouldShowSegments}
                      primarySegmentDefined={primarySegmentDefined}
                      setPrimarySegmentDefined={setPrimarySegmentDefined}
                      values={values}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      editPrimarySegment={editPrimarySegment}
                      setEditPrimarySegment={setEditPrimarySegment}
                      isValid={isValid}
                      dirty={dirty}
                    />
                    <PopulationSegment
                      shouldShowSegments={shouldShowSegments}
                      populationSegmentDefined={populationSegmentDefined}
                      setPopulationSegmentDefined={setPopulationSegmentDefined}
                      values={values}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      editPopulationSegment={editPopulationSegment}
                      setEditPopulationSegment={setEditPopulationSegment}
                      isValid={isValid}
                      dirty={dirty}
                    />
                    <ComparisonSegment
                      shouldShowSegments={shouldShowSegments}
                      comparisonSegmentDefined={comparisonSegmentDefined}
                      setComparisonSegmentDefined={setComparisonSegmentDefined}
                      values={values}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      editComparisonSegment={editComparisonSegment}
                      setEditComparisonSegment={setEditComparisonSegment}
                      isValid={isValid}
                      dirty={dirty}
                    />
                  </div>
                </form>
                <footer
                  className="h-20 bg-gray-50 flex justify-between items-center px-10 py-7 fixed bottom-0 right-0 left-24"
                  data-testid="review-request-footer"
                >
                  <BlankButton
                    data-testid="custom-analysis-cancel-button"
                    onClick={cancelAnalysisRequest}
                  >
                    Cancel
                  </BlankButton>
                  <PrimaryButton
                    className="disabled:bg-[#E36006] disabled:text-white disabled:opacity-50"
                    data-testid="request-custom-analysis-button"
                    disabled={!primarySegmentDefined}
                    onClick={handleRequestAnalysis}
                  >
                    Request Analysis
                  </PrimaryButton>
                </footer>
              </div>
            )}
          </Formik>
        </div>
      </div>
    </main>
  );
};
